import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import {
  Component,
  ElementRef,
  OnDestroy,
  ChangeDetectorRef,
  Renderer2,
  ChangeDetectionStrategy,
} from "@angular/core";

import { DatePipe, DecimalPipe } from "@angular/common";

import { TranslateService } from "@ngx-translate/core";

import * as CubiqTS from "shared/cubiq-model-multi-asset";

import {
  UserPromiseLookupService,
  AccountPromiseLookupService,
  ClientPromiseLookupService,
  TraderGroupPromiseLookupService,
  SalesGroupPromiseLookupService,
  CcyPromiseLookupService,
  CcyPairPromiseLookupService,
  CurrencyPairGroupPromiseLookupService,
  BranchPromiseLookupService,
  SysFeatures,
  FixingSourcePromiseLookupService,
  BenchmarkFixingPrimaryLookupService,
  CustomFieldLookupService,
} from "shared/cubiq-services";

import { ActiveUserProfilePromiseService } from "shared/auth-services";
import { ActiveUser, OrdersTableDefinitionFactory } from "shared/cubiq-services";

import { BfxBlotterFilterService } from "../bfx-blotter-filter/bfx-blotter-filter.service";

import { BfxBlotterFilterSelectorService } from "../bfx-blotter-filter/bfx-blotter-filter-selector.service";
import { BfxFindFilterView } from "../bfx-find/bfx-find-filter-view";
import { BfxFindOrdersFilterFieldName, SystemConfigPromiseService } from "shared/cubiq-services";
import { BfxPanelHelperService } from "../bfx-common/services/bfx-panel-helper.service";
import { BfxFindOrdersService } from "../bfx-find-orders/bfx-find-orders.service";

import { BfxFindOrdersFilterDataFactory } from "./bfx-find-orders-filter-data-factory";

import {
  FIND_OBJECT_SORT,
  FilterData,
  BFX_FIND_TIMEZONE_CODES,
  BFX_FIND_FORWARD_FIRST_CODES,
} from "../bfx-find/bfx-find.constants";

import { DomHandler } from "@bfx/primeng/dom";

import { BfxColDef } from "shared/cubiq-services/model/TableDefinition";

@Component({
  selector: "bfx-find-orders-filter-view",
  templateUrl: "../bfx-find/html/bfx-find-filter-view.html",
  providers: [DatePipe, DecimalPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class BfxFindOrdersFilterViewComponent extends BfxFindFilterView implements OnDestroy {
  protected primaryTypeName = CubiqTS.OrderDataImpl.enType;

  constructor(
    blotterService: BfxBlotterFilterService,
    translationService: TranslateService,
    userService: UserPromiseLookupService,
    accountService: AccountPromiseLookupService,
    clientService: ClientPromiseLookupService,
    traderGroupService: TraderGroupPromiseLookupService,
    salesGroupService: SalesGroupPromiseLookupService,
    branchLookupService: BranchPromiseLookupService,
    ccyService: CcyPromiseLookupService,
    ccyPairService: CcyPairPromiseLookupService,
    currencyPairGroupService: CurrencyPairGroupPromiseLookupService,
    datePipe: DatePipe,
    activeUserService: ActiveUserProfilePromiseService,
    selectorService: BfxBlotterFilterSelectorService,
    panelHelper: BfxPanelHelperService,
    domHandler: DomHandler,
    cd: ChangeDetectorRef,
    decimalFormatter: DecimalPipe,
    systemConfigLookup: SystemConfigPromiseService,
    protected findService: BfxFindOrdersService,
    protected fixingSourceService: FixingSourcePromiseLookupService,
    protected bmFixingSourceService: BenchmarkFixingPrimaryLookupService,
    renderer: Renderer2,
    el: ElementRef,
    //
    ordersDefinition: OrdersTableDefinitionFactory,
    filterDataFactory: BfxFindOrdersFilterDataFactory,
    customFieldService: CustomFieldLookupService,
  ) {
    super(
      blotterService,
      translationService,
      userService,
      accountService,
      clientService,
      traderGroupService,
      salesGroupService,
      branchLookupService,
      ccyService,
      ccyPairService,
      currencyPairGroupService,
      datePipe,
      activeUserService,
      selectorService,
      panelHelper,
      domHandler,
      cd,
      decimalFormatter,
      systemConfigLookup,
      findService,
      fixingSourceService,
      bmFixingSourceService,
      customFieldService,
      renderer,
      el,
    );
    this.async_data = new Promise<FilterData[]>((res) => {
      let rolePromises: Array<Promise<any>> = [];
      rolePromises.push(
        this.systemConfigLookup.getSystemConfig(SysFeatures.clientTraderUsersEnabled),
      );
      rolePromises.push(this.systemConfigLookup.getSystemConfig(SysFeatures.fwdEnabled));
      rolePromises.push(this.systemConfigLookup.getSystemConfig(SysFeatures.ndfEnabled));
      rolePromises.push(this.systemConfigLookup.getSystemConfig(SysFeatures.benchmarksEnabled));
      Promise.all(rolePromises).then((values: Array<any>) => {
        this.clientTraderUsersEnabled = values[0].toUpperCase() == "Y";
        this.fwdEnabled = values[1].toUpperCase() == "Y";
        this.ndfEnabled = values[2].toUpperCase() == "Y";
        this.benchmarksEnabled = values[3].toUpperCase() == "Y";

        ordersDefinition.defineTable().then((def) => {
          this.tableDefinition = def;
          this.initTableDefinition(this.tableDefinition);
          this.initCustomFields();
          this.initNonTableFindFields();
          let { data } = filterDataFactory.defineFilterData(
            this.clientTraderUsersEnabled,
            this.fwdEnabled,
            this.ndfEnabled,
            this.benchmarksEnabled,
          );

          this.data = data;
          this.isDataPrimed = true;
          res(this.data);
        });
      });
    });

    this.filterRequestObservableSubscr = this.findService
      .getFilterRequestObservable()
      .subscribe(() => {
        // TODO clone
        this.findService.responseFilterView(this.data);
      });
  }

  public initNonTableFindFields() {
    this.columnFilterTypes[BfxFindOrdersFilterFieldName.ORDER_ID] = "STRING";
    this.columnFilterTypes[BfxFindOrdersFilterFieldName.TRADE_ID] = "STRING";
    this.columnFilterTypes[BfxFindOrdersFilterFieldName.STRATEGY_ID] = "STRING";
    this.columnFilterTypes[BfxFindOrdersFilterFieldName.EXTERNAL_REF] = "STRING";
    this.columnFilterTypes[BfxFindOrdersFilterFieldName.BOOKING_UTI] = "STRING";
    this.columnFilterTypes[BfxFindOrdersFilterFieldName.NETTING_ID] = "STRING";
    this.columnFilterTypes[BfxFindOrdersFilterFieldName.FWD_NETTING_ID] = "STRING";
    this.columnFilterTypes[BfxFindOrdersFilterFieldName.CLIENT_TRADER] = "CLIENT_TRADER";
    this.columnFilterTypes[BfxFindOrdersFilterFieldName.CCY] = "CURRENCY";
    this.columnFilterTypes[BfxFindOrdersFilterFieldName.CCY_PAIR_GROUP] = "CURRENCY_PAIR_GROUP";
    this.columnFilterTypes[BfxFindOrdersFilterFieldName.SEARCH_IN_TZ] = "TIMEZONE_CD";
    this.columnFilterTypes[BfxFindOrdersFilterFieldName.ENTRY_DATE] = "DATE";
    // this.columnFilterTypes[BfxFindOrdersFilterFieldName.ENTRY_USER] = "STRING";
    this.columnFilterTypes[BfxFindOrdersFilterFieldName.MODIFY_DATE] = "DATE";
    // this.columnFilterTypes[BfxFindOrdersFilterFieldName.MODIFY_USER] = "STRING";
    this.columnFilterTypes[BfxFindOrdersFilterFieldName.CANCEL_DATE] = "DATE";
    // this.columnFilterTypes[BfxFindOrdersFilterFieldName.CANCEL_USER] = "STRING";
    this.columnFilterTypes[BfxFindOrdersFilterFieldName.EXPIRED_DATE] = "DATE";
    this.columnFilterTypes[BfxFindOrdersFilterFieldName.REQUEST_RT] = "RATE";
    this.columnFilterTypes[BfxFindOrdersFilterFieldName.BM_FIXCODE] = "BM_FIXCODE";
    this.columnFilterTypes[BfxFindOrdersFilterFieldName.FIXING_TIME] = "TIME";
    this.columnFilterTypes[BfxFindOrdersFilterFieldName.FIXING_DATE] = "DATE";

    this.columnFilterTypes[BfxFindOrdersFilterFieldName.OBO_CLIENT] = "STRING";
    this.columnFilterTypes[BfxFindOrdersFilterFieldName.OBO_ACCOUNT] = "STRING";

    if (this.benchmarksEnabled) {
      this.bmFixingSourceService.isPrimed().then(() => {
        this.bmFixingSourceService.getAll().then((values: Array<CubiqTS.BenchmarkFixingImpl>) => {
          if (values) {
            let primedData = values
              .map((a: CubiqTS.BenchmarkFixingImpl) => {
                return { label: a.benchmarkCd, value: a.benchmarkCd };
              })
              .sort(FIND_OBJECT_SORT);
            this.objectValues[BfxFindOrdersFilterFieldName.BM_FIXCODE] = primedData;
            this.objectColumns[BfxFindOrdersFilterFieldName.BM_FIXCODE] = true;
          }
        });
      });

      let booleanFieldData = [{ label: "True", value: true } /*,{ label: "False", value: false}*/];
      this.columnFilterTypes[BfxFindOrdersFilterFieldName.BM_SPAWNED] = "TRUE_FALSE";
      this.objectValues[BfxFindOrdersFilterFieldName.BM_SPAWNED] = booleanFieldData;
      this.objectColumns[BfxFindOrdersFilterFieldName.BM_SPAWNED] = true;
    }

    let timezoneCdFieldData = BFX_FIND_TIMEZONE_CODES;
    this.columnFilterTypes[BfxFindOrdersFilterFieldName.SEARCH_IN_TZ] = "TIMEZONE_CD";
    this.objectValues[BfxFindOrdersFilterFieldName.SEARCH_IN_TZ] = timezoneCdFieldData;
    this.objectColumns[BfxFindOrdersFilterFieldName.SEARCH_IN_TZ] = true;

    const forwardFirstFieldData = BFX_FIND_FORWARD_FIRST_CODES;
    this.columnFilterTypes[BfxFindOrdersFilterFieldName.BM_FORWARD_FIRST] = "FORWARD_FIRST";
    this.objectValues[BfxFindOrdersFilterFieldName.BM_FORWARD_FIRST] = forwardFirstFieldData;
    this.objectColumns[BfxFindOrdersFilterFieldName.BM_FORWARD_FIRST] = true;

    this.ccyService.isPrimed().then(() => {
      this.ccyService.getAll().then((values: Array<CubiqTS.CurrencyImpl>) => {
        if (values) {
          let primedData = values
            .map((a) => {
              return { label: a.id, value: a.id };
            })
            .sort(FIND_OBJECT_SORT);
          this.objectValues[BfxFindOrdersFilterFieldName.CCY] = primedData;
          this.objectColumns[BfxFindOrdersFilterFieldName.CCY] = true;
        }
      });
    });

    this.ccyPairService.isPrimed().then(() => {
      this.currencyPairGroupService
        .getAll()
        .then((values: Array<CubiqTS.CurrencyPairGroupImpl>) => {
          if (values) {
            let primedData = values
              .map((a: CubiqTS.CurrencyPairGroup) => {
                return {
                  label: a.currencypairGroupNm,
                  value: a.groupMembership.map((m: CubiqTS.CurrencyPairGroupMember) => {
                    return {
                      label: `${m.baseCurrencyCd}/${m.nonBaseCurrencyCd}`,
                      value: `${m.baseCurrencyCd}${m.nonBaseCurrencyCd}`,
                    };
                  }),
                };
              })
              .sort(FIND_OBJECT_SORT);

            this.objectValues[BfxFindOrdersFilterFieldName.CCY_PAIR_GROUP] = primedData;
            this.objectColumns[BfxFindOrdersFilterFieldName.CCY_PAIR_GROUP] = true;
          }
        });
    });

    this.systemConfigLookup.getSystemConfig(SysFeatures.displayUserNames).then((cfg) => {
      this.showUserName = cfg == "true" || cfg == "TRUE" || cfg == "1" || cfg == "Y" || cfg == "y";

      this.userService.isPrimed().then(() => {
        this.userService.getAll().then((values: Array<CubiqTS.UserImpl>) => {
          this.activeUserService.getActiveUserData().then((activeUser: ActiveUser) => {
            this.primeClientTraderUserData(
              this.ALL_CLIENT_TRADERS,
              BfxFindOrdersFilterFieldName.CLIENT_TRADER,
              values,
              activeUser,
            );
            this.primeUserData(BfxFindOrdersFilterFieldName.ENTRY_USER, values, activeUser);
            this.primeUserData(BfxFindOrdersFilterFieldName.MODIFY_USER, values, activeUser);
            this.primeUserData(BfxFindOrdersFilterFieldName.CANCEL_USER, values, activeUser);
          });
        });
      });
    });
  }

  public primeTraderGroupData(columnDef: BfxColDef, values: CubiqTS.TradingGroupImpl[]): void {
    let primedData = values
      .map((a) => {
        return { label: a.groupCode, value: a.groupId };
      })
      .sort(FIND_OBJECT_SORT);
    let columnDefs: Array<BfxColDef> = this.tableDefinition.columns.filter(
      (c) => c.columnFilterType == columnDef.columnFilterType,
    );
    columnDefs.forEach((c) => {
      this.objectValues[c.field] = primedData;
    });
  }

  public primeOrderTypeData(columnDef: BfxColDef, values: any[]): void {
    let primedData = values.sort(FIND_OBJECT_SORT);
    this.objectValues[columnDef.field] = primedData;
  }

  public selectOption(data: any, event: any) {
    super.selectOption(data, event);

    if (data.fieldName == BfxFindOrdersFilterFieldName.CLIENT_ID) {
      this.cacheSelectedClientsIds(data, BfxFindOrdersFilterFieldName.CLIENT_TRADER);
    } else if (data.fieldName == BfxFindOrdersFilterFieldName.CLIENT_NAME) {
      this.cacheSelectedClientsNames(data, BfxFindOrdersFilterFieldName.CLIENT_TRADER);
    }
  }

  public resetVisibleClientTraderData(): void {
    this.cacheSelectedClientsIds({ valid: null }, BfxFindOrdersFilterFieldName.CLIENT_TRADER);
    this.cacheSelectedClientsNames({ valid: null }, BfxFindOrdersFilterFieldName.CLIENT_TRADER);
  }

  /**
   * For columns with objectValues, return true if only a single value can be selected
   * @param columnName
   * @returns
   */
  public isSingleSelectColumn(columnName: string): boolean {
    switch (columnName) {
      case BfxFindOrdersFilterFieldName.BM_FORWARD_FIRST:
        return true;
      default:
        return false;
    }
  }
}
